/**
 * PopOutGate — forcing-function landing that appears when the app launches
 * in the Teams desktop MAIN window.
 *
 * Bill 2026-04-23 design: running our full workspace inside the main Teams
 * window hides the rest of Teams chrome (Chat, Calls, channels) that FSRs
 * still need mid-flow. Rather than rely on officers remembering to pop
 * out, intercept the launch: show a compact diagnostic card with one
 * prominent "Open in new window" button. When the popout succeeds, the
 * main window is navigated to Chat so Teams returns to its normal role
 * and the Retail app runs in its own window.
 *
 * Bill 2026-10-14 (the LOP/LWP pattern): the pop-out is now SEMI-AUTOMATIC — on launch in the
 * main window it opens straight away, without waiting for the click; the card only stays when
 * Teams refuses. A deep link's target ("cart:<guid>") is carried into the pop-out on its URL
 * (?open=…), because Teams does not pass subEntityId into a Stageview window.
 *
 * Rendered ONLY for `env === 'teams-desktop-main'`. Every other host
 * (desktop-popout / web / mobile / browser-dev) bypasses the gate and
 * renders the real app immediately — popout only exists on desktop.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { ExternalLink, ArrowRight } from '@retail-ops/panel-shell/fi';
import { APP_NAME, APP_SUBTITLE } from '@/constants/version';
import { SystemAboutPanel } from '@/components/common/SystemAboutPanel';
import pcbLogoUrl from '@/assets/phelps-co-bank-logo-white.svg';
import {
  detectHostEnvironment,
  popoutCurrent,
  type HostEnvironmentInfo,
} from '@/services/teamsMultiWindow';
import { deepLinkFromTeams, deepLinkFromUrl } from '@/hooks/useTeamsDeepLink';
import '@/styles/popout-gate.css';

type GateState =
  | { kind: 'checking' }
  | { kind: 'allow' }                         // non-desktop-main → render app
  | { kind: 'gate'; host: HostEnvironmentInfo }
  | { kind: 'opening'; host: HostEnvironmentInfo }
  | { kind: 'opened'; host: HostEnvironmentInfo }   // popout spawned → success card
  | { kind: 'error'; host: HostEnvironmentInfo; message: string };

export function PopOutGate({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<GateState>({ kind: 'checking' });
  const [target, setTarget] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const host = await detectHostEnvironment();
      if (cancelled) return;
      if (host.env !== 'teams-desktop-main') { setState({ kind: 'allow' }); return; }
      // Semi-automatic pop-out: try at once, carrying any deep-link target.
      const t = deepLinkFromUrl() ?? (await deepLinkFromTeams());
      if (cancelled) return;
      setTarget(t);
      setState({ kind: 'opening', host });
      const r = await popoutCurrent(t);
      if (cancelled) return;
      setState(r.ok ? { kind: 'opened', host } : { kind: 'gate', host });
    })();
    return () => { cancelled = true; };
  }, []);

  async function handlePopOut(): Promise<void> {
    if (state.kind !== 'gate' && state.kind !== 'error') return;
    setState({ kind: 'opening', host: state.host });

    const popoutResult = await popoutCurrent(target);
    if (!popoutResult.ok) {
      setState({
        kind: 'error',
        host: state.host,
        message: popoutResult.reason === 'not-desktop'
          ? 'Pop-out requires Teams desktop. Continue in this window.'
          : popoutResult.reason === 'already-popout'
            ? 'Already in a pop-out window.'
            : `Could not open a new window: ${popoutResult.error ?? 'unknown error'}. You can continue in this window.`,
      });
      return;
    }

    // Popout spawned. Show a success card in the main window instead of
    // auto-navigating host — pages.navigateToApp was dropping the main
    // window on "There was a problem reaching this app" in practice.
    // User can pick any other Teams sidebar item themselves.
    setState({ kind: 'opened', host: state.host });
  }

  if (state.kind === 'checking') {
    // Brief pre-detection flash — SDK init + getContext is usually <200 ms.
    return (
      <div className="popout-gate__splash">
        <span>Preparing PCB Retail…</span>
      </div>
    );
  }

  if (state.kind === 'allow') {
    return <>{children}</>;
  }

  // gate / opening / opened / error
  const host = state.host;
  const busy = state.kind === 'opening';
  const opened = state.kind === 'opened';
  const errorMessage = state.kind === 'error' ? state.message : null;

  const continueAnyway = (): void => setState({ kind: 'allow' });

  return (
    <div className="popout-gate" role="main">
      <div className="popout-gate__card">
        <header className="popout-gate__header">
          <img src={pcbLogoUrl} alt="PCB" className="popout-gate__logo" />
          <div className="popout-gate__brand">
            <span className="popout-gate__brand-name">{APP_NAME}</span>
            <span className="popout-gate__brand-sub">{APP_SUBTITLE}</span>
          </div>
        </header>

        <div className="popout-gate__message">
          {opened ? (
            <>
              <h1>Retail is open in a new window</h1>
              <p>
                Look for the dedicated Retail window on your taskbar. You can
                close this tab or switch to Chat, Calls, Activity, or any
                other Teams sidebar item to keep using Teams normally.
              </p>
            </>
          ) : (
            <>
              <h1>Open this in its own window</h1>
              <p>
                PCB Retail works best in a dedicated window so you can keep chatting,
                taking calls, and using the rest of Teams while you work with
                customers. Click below to move Retail into its own window —
                Teams stays behind for Chat, Calls, and channels.
              </p>
            </>
          )}
        </div>

        {errorMessage && (
          <div className="popout-gate__banner" role="alert">
            {errorMessage}
          </div>
        )}

        {!opened && (
          <button
            type="button"
            className="popout-gate__cta"
            onClick={handlePopOut}
            disabled={busy}
            autoFocus
          >
            <ExternalLink size={18} aria-hidden />
            {busy ? 'Opening new window…' : 'Open in new window'}
            <ArrowRight size={18} aria-hidden />
          </button>
        )}

        {/* ONE About panel everywhere (Bill 2026-09-11): this gate, Help ->
            About and the user menu all render SystemAboutPanel, so the gate
            cannot show a different Core Director status than the app does. It
            also adds LexisNexis and USPS, which this card never had. */}
        <div className="popout-gate__diag">
          <SystemAboutPanel
            host={state.host}
            showRefresh={false}
          />
        </div>

        {state.kind === 'error' && (
          <button
            type="button"
            className="popout-gate__escape"
            onClick={continueAnyway}
          >
            Continue in this window anyway →
          </button>
        )}
      </div>
    </div>
  );
}

export default PopOutGate;
